import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  HeaderGlobalAction,
  HeaderPanel,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
  Button,
} from "@carbon/react";
import { Notification, UserAvatar } from "@carbon/icons-react";
import { useAuth, useUser } from "../../../context";
import { userCompanyTypeMap } from "../../../types";
import { ABILITIES } from "../../../utils";
import { NotificationInterface } from "../../../types/notification.type";
import {
  useNotifications,
  useUnreadNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from "../../../hooks/useNotifications";
import Notifications from "../../Notifications/Notifications";
import "./headerUserActions.scss";

type OpenPanel = "notifications" | "user" | null;

/**
 * The full-Carbon replacement for the pre-Carbon (headlessui/Tailwind)
 * `UserMobileMenu`: two `HeaderGlobalAction`s in the dark UI-shell global bar —
 * a notifications bell (with an unread badge) and a user avatar — each toggling
 * a Carbon `HeaderPanel`. Follows the Carbon global-header pattern
 * (https://carbondesignsystem.com/patterns/global-header/): icon actions on the
 * right, panels sliding in beneath the header. Notification list + mutations
 * are reused verbatim (`Notifications`, `useNotifications` &co.), only the
 * chrome around them changed.
 */
const HeaderUserActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user, isBuy, can, companyChangeStatus, purchaseOrderModuleEnabled } =
    useUser();

  const [open, setOpen] = useState<OpenPanel>(null);
  const [notificationsPage, setNotificationsPage] = useState(0);

  const { data: notifications, refetch } = useNotifications(notificationsPage);
  const { data: unreadCount, refetch: refetchUnread } =
    useUnreadNotifications();
  const markNotificationAsRead = useMarkNotificationAsRead();
  const markAllNotificationsAsRead = useMarkAllNotificationsAsRead();

  // Poll notifications on the same 60s cadence the pre-Carbon menu used.
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
      refetchUnread();
    }, 60000);
    return () => clearInterval(interval);
  }, [refetch, refetchUnread]);

  // Preserved verbatim from UserMobileMenu: a successful company change reloads.
  if (companyChangeStatus) {
    if (companyChangeStatus.status === 200) {
      window.location.reload();
    }
    companyChangeStatus.status = null;
  }

  const unread =
    typeof unreadCount === "number" ? unreadCount : unreadCount?.count || 0;

  const toggle = (panel: Exclude<OpenPanel, null>) =>
    setOpen((prev) => (prev === panel ? null : panel));
  const close = () => setOpen(null);

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead.mutateAsync();
  };

  const extractRedirectPath = (linkUrl: string): string | null => {
    try {
      const params = new URLSearchParams(
        linkUrl.startsWith("?") ? linkUrl : linkUrl.replace(/^.*\?/, "?")
      );
      const redirectURL = params.get("redirectURL");
      if (!redirectURL) return null;
      params.delete("redirectURL");
      const remaining = params.toString();
      return remaining ? `${redirectURL}?${remaining}` : redirectURL;
    } catch {
      return null;
    }
  };

  const handleNotificationClick = async (
    notification: NotificationInterface
  ) => {
    if (notification.linkUrl) {
      const path = extractRedirectPath(notification.linkUrl);
      if (path) {
        close();
        navigate(path);
      }
    }
    if (!notification.read) {
      await markNotificationAsRead.mutateAsync(notification.id);
    }
  };

  const go = (path: string) => {
    close();
    navigate(path);
  };

  const roleLabel = user?.userCompanyType
    ? userCompanyTypeMap[
        user.userCompanyType as keyof typeof userCompanyTypeMap
      ]
    : "";

  return (
    <>
      <HeaderGlobalAction
        aria-label={t("notification.title", "Notifications")}
        isActive={open === "notifications"}
        onClick={() => toggle("notifications")}
        tooltipAlignment="end"
        className="header-user-actions__bell"
      >
        <Notification size={20} />
        {unread > 0 ? (
          <span className="header-user-actions__badge" aria-hidden="true">
            {unread}
          </span>
        ) : null}
      </HeaderGlobalAction>

      <HeaderGlobalAction
        aria-label={user?.fullName || t("profile.title")}
        isActive={open === "user"}
        onClick={() => toggle("user")}
        tooltipAlignment="end"
      >
        <UserAvatar size={20} />
      </HeaderGlobalAction>

      <HeaderPanel
        aria-label={t("notification.title", "Notifications")}
        expanded={open === "notifications"}
        className="header-user-actions__panel"
      >
        <div className="header-user-actions__panel-head">
          <span className="header-user-actions__panel-title">
            {t("notification.title", "Notifications")}
          </span>
          <Button
            kind="ghost"
            size="sm"
            onClick={handleMarkAllAsRead}
            disabled={unread === 0 || markAllNotificationsAsRead.isPending}
          >
            {t("notification.markAllAsRead", "Mark all as read")}
          </Button>
        </div>
        <div className="header-user-actions__notifications">
          {notifications ? (
            <Notifications
              notifications={notifications}
              handleNotificationClick={handleNotificationClick}
              notificationsPage={notificationsPage}
              setNotificationsPage={setNotificationsPage}
            />
          ) : null}
        </div>
      </HeaderPanel>

      <HeaderPanel
        aria-label={t("profile.title")}
        expanded={open === "user"}
        className="header-user-actions__panel"
      >
        <div className="header-user-actions__user-head">
          <UserAvatar size={20} />
          <span className="header-user-actions__user-name">
            {user?.fullName}
            {roleLabel ? (
              <span className="header-user-actions__user-role">
                {" "}
                - ({roleLabel})
              </span>
            ) : null}
          </span>
        </div>
        <Switcher aria-label={t("profile.title")}>
          <SwitcherItem
            aria-label={t("profile.title")}
            onClick={() => go("/profile")}
          >
            {t("profile.title")}
          </SwitcherItem>
          {isBuy && can(ABILITIES.VIEW_SITES) ? (
            <SwitcherItem
              aria-label={t("profile.sites")}
              onClick={() => go("/sites")}
            >
              {t("profile.sites")}
            </SwitcherItem>
          ) : null}
          {isBuy && can(ABILITIES.VIEW_SUPPLIERS) ? (
            <SwitcherItem
              aria-label={t("profile.delegate")}
              onClick={() => go("/delegate")}
            >
              {t("profile.delegate")}
            </SwitcherItem>
          ) : null}
          {/* Roles management is side-aware (buyer MANAGE_ROLES / seller
              SELLER_MANAGE_ROLES), so it is NOT isBuy-gated — a seller admin
              reaches the same /roles page, which the backend scopes to their side. */}
          {can(ABILITIES.MANAGE_ROLES) || can(ABILITIES.SELLER_MANAGE_ROLES) ? (
            <SwitcherItem
              aria-label={t("profile.roles")}
              onClick={() => go("/roles")}
            >
              {t("profile.roles")}
            </SwitcherItem>
          ) : null}
          {/* Purchase-order approval rules live in the account menu (not the side nav):
              a rarely-used admin setting. Needs BOTH the OC module and the ability. */}
          {isBuy &&
          purchaseOrderModuleEnabled &&
          can(ABILITIES.MANAGE_PURCHASE_ORDER_APPROVALS) ? (
            <SwitcherItem
              aria-label={t("purchaseOrders.approvalRules.nav")}
              onClick={() => go("/purchase-orders/approval-rules")}
            >
              {t("purchaseOrders.approvalRules.nav")}
            </SwitcherItem>
          ) : null}
          {isBuy &&
          purchaseOrderModuleEnabled &&
          can(ABILITIES.MANAGE_PURCHASE_ORDER_APPROVALS) ? (
            <SwitcherItem
              aria-label={t("purchaseOrders.settings.nav")}
              onClick={() => go("/purchase-orders/settings")}
            >
              {t("purchaseOrders.settings.nav")}
            </SwitcherItem>
          ) : null}
          <SwitcherDivider />
          <SwitcherItem
            aria-label={t("profile.signOff")}
            onClick={() => {
              close();
              logout();
              navigate("/");
            }}
          >
            {t("profile.signOff")}
          </SwitcherItem>
        </Switcher>
      </HeaderPanel>
    </>
  );
};

export default HeaderUserActions;
